<template>
  <MetricsSummaryWithLoader
    :loading="loading"
    :number-of-summary-skeletons="4"
    totals-label-width="208px"
    :summaries="summaries"
    :project-name="projectName"
    :date-range="dateRange"
  />
</template>

<script setup>
import MetricsSummaryWithLoader from '@/components/insights/Metrics/MetricsSummaryWithLoader.vue';
import { computed } from 'vue';
import { isEmpty } from 'lodash-es';
import { formatTooltipValue } from '@/helpers/formatters';
import { useColors } from '@/composables/colors';

const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  data: {
    type: Array,
    default: () => ([]),
  },
  tooltips: {
    type: Object,
    default: () => ({}),
  },
  projectName: {
    type: String,
    default: '',
  },
  dateRange: {
    type: Object,
    default: () => ({}),
  },
  isGeneralVsNiche: {
    type: Boolean,
    default: false,
  },
});

const { brandPrimary } = useColors();

const summaries = computed(() => {
  if (isEmpty(props.data)) return [];

  const free = (props.data || []).find((item) => item.category === 'free') || {};
  const paid = (props.data || []).find((item) => item.category === 'paid') || {};
  const other = (props.data || []).find((item) => item.category === 'other') || {};
  const general = (props.data || []).find((item) => item.category === 'general') || {};
  const niche = (props.data || []).find((item) => item.category === 'niche') || {};

  const totalFreeApplications = free?.review || 0;
  const totalPaidApplications = paid?.review || 0;
  const totalOtherApplications = other?.review || 0;
  const totalGeneralApplications = general?.review || 0;
  const totalNicheApplications = niche?.review || 0;
  const totalFreeHires = free?.hired || 0;
  const totalPaidHires = paid?.hired || 0;
  const totalOtherHires = other?.hired || 0;
  const totalGeneralHires = general?.hired || 0;
  const totalNicheHires = niche?.hired || 0;

  const totalApplications = props.isGeneralVsNiche
    ? totalGeneralApplications + totalNicheApplications + totalOtherApplications
    : totalFreeApplications + totalPaidApplications + totalOtherApplications;
  const totalHires = props.isGeneralVsNiche
    ? totalGeneralHires + totalNicheHires + totalOtherHires
    : totalFreeHires + totalPaidHires + totalOtherHires;

  const baseSummaries = [];

  if (props.isGeneralVsNiche) {
    baseSummaries.push(
      {
        id: 'general_applications',
        tooltipKey: 'inbound-apps-from-general-sources',
        label: 'Applications from General Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof general?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((general.review / totalApplications) * 100, 'percent')
          : '-',
      },
      {
        id: 'general_hired',
        tooltipKey: 'inbound-hires-from-general-sources',
        label: 'Hires from General Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof general?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((general.hired / totalHires) * 100, 'percent')
          : '-',
      },
      {
        id: 'niche_applications',
        tooltipKey: 'inbound-apps-from-niche-sources',
        label: 'Applications from Niche Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof niche?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((niche.review / totalApplications) * 100, 'percent')
          : '-',
      },
      {
        id: 'niche_hired',
        tooltipKey: 'inbound-hires-from-niche-sources',
        label: 'Hires from Niche Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof niche?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((niche.hired / totalHires) * 100, 'percent')
          : '-',
      },
    );
  } else {
    baseSummaries.push(
      {
        id: 'free_applications',
        tooltipKey: 'inbound-apps-from-free-sources',
        label: 'Applications from Free Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof free?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((free.review / totalApplications) * 100, 'percent')
          : '-',
      },
      {
        id: 'free_hired',
        tooltipKey: 'inbound-hires-from-free-sources',
        label: 'Hires from Free Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof free?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((free.hired / totalHires) * 100, 'percent')
          : '-',
      },
      {
        id: 'paid_applications',
        tooltipKey: 'inbound-apps-from-paid-sources',
        label: 'Applications from Paid Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof paid?.review === 'number' && totalApplications > 0
          ? formatTooltipValue((paid.review / totalApplications) * 100, 'percent')
          : '-',
      },
      {
        id: 'paid_hired',
        tooltipKey: 'inbound-hires-from-paid-sources',
        label: 'Hires from Paid Inbound Sources',
        color: brandPrimary.value,
        value:
        typeof paid?.hired === 'number' && totalHires > 0
          ? formatTooltipValue((paid.hired / totalHires) * 100, 'percent')
          : '-',
      },
    );
  }

  return baseSummaries.map((summary) => {
    const { body_r: html, mode } = props.tooltips?.[summary.tooltipKey] || {};
    return {
      ...summary,
      tooltip: { html, mode, key: summary.tooltipKey },
    };
  });
});
</script>
